import { useMemo, useState } from 'react'

import InfiniteScroll from 'react-infinite-scroller'

import { SpinnerIcon } from '@bettermode/icons/solid/Spinner'
import {
  Chat,
  ChatParticipant,
  ChatParticipantRole,
  ChatParticipantStatus,
} from '@tribeplatform/gql-client/types'
import { useChatParticipants } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { T } from '../../i18n/index.js'

import { AddMemberModal } from './AddMemberModal.js'
import { GroupParticipantsListItem } from './GroupParticipantsListItem.js'
import { ParticipantsLoadingIndicator } from './ParticipantsLoadingIndicator.js'

const PARTICIPANTS_LIMIT = 10

interface Props {
  chat: Chat
  onParticipantClick: (participant: ChatParticipant) => void
}

export const GroupParticipantsList = ({ chat, onParticipantClick }: Props) => {
  const [openAddMemberModal, setOpenAddMemberModal] = useState(false)
  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage = false,
    isLoading,
  } = useChatParticipants({
    variables: {
      chatId: chat._id,
      limit: PARTICIPANTS_LIMIT,
    },
  })
  const participants = useMemo(() => {
    const pages = data?.pages ?? []
    const items = pages.flatMap(page => page.data)
    const activePariticpants = items.filter(
      item => item.status === ChatParticipantStatus.Active,
    )
    // Sort by role priority: Owner -> Admin -> Others
    const sortedParticipants = activePariticpants.sort((a, b) => {
      if (a.role === ChatParticipantRole.Owner) {
        return -1
      }
      if (b.role === ChatParticipantRole.Owner) {
        return 1
      }

      if (a.role === ChatParticipantRole.Admin) {
        return -1
      }
      if (b.role === ChatParticipantRole.Admin) {
        return 1
      }

      return 0
    })

    return sortedParticipants
  }, [data])
  const currentParticipant = useMemo(() => {
    return participants.find(
      participant => participant.memberId === chat.currentParticipant?.memberId,
    )
  }, [participants, chat.currentParticipant])
  const canAddMembers =
    currentParticipant?.role === ChatParticipantRole.Admin ||
    currentParticipant?.role === ChatParticipantRole.Owner

  return (
    <div className="flex flex-col relative">
      <div className="p-3 flex justify-between items-center gap-2">
        <div className="text-sm text-content-muted">
          <T
            id="Messaging.GroupMembersField.Label"
            defaultMessage="Group members"
          />
        </div>
        {canAddMembers && (
          <Button
            variant="tertiaryNeutral"
            size="sm"
            leadingIcon={<SvgIcon name="plus" />}
            onClick={() => setOpenAddMemberModal(true)}
          >
            <T id="Generics.Add" defaultMessage="Add" />
          </Button>
        )}
      </div>
      <div className="p-3 pt-0">
        <InfiniteScroll
          pageStart={0}
          useWindow={false}
          hasMore={hasNextPage || false}
          loadMore={() => {}}
        >
          <ul>
            {participants.map(participant => (
              <li key={participant._id}>
                <GroupParticipantsListItem
                  chat={chat}
                  currentParticipant={currentParticipant}
                  otherParticipant={participant}
                  onClick={() => onParticipantClick(participant)}
                />
              </li>
            ))}
            {isLoading ? <ParticipantsLoadingIndicator /> : null}
            {hasNextPage || isFetchingNextPage ? (
              <li className="pt-1">
                <div
                  className="hover:bg-surface-hovered p-2 rounded-base flex items-center gap-2 cursor-pointer text-sm transition-colors duration-300 text-content-muted"
                  onClick={() => fetchNextPage()}
                >
                  {isFetchingNextPage ? (
                    <SpinnerIcon className="animate-spin h-4 w-4" />
                  ) : (
                    <T id="Generics.LoadMore" defaultMessage="Load more" />
                  )}
                </div>
              </li>
            ) : null}
          </ul>
        </InfiniteScroll>
      </div>
      <AddMemberModal
        chatId={chat._id}
        currentParticipantCount={chat.participantCount ?? participants.length}
        open={openAddMemberModal}
        onClose={() => setOpenAddMemberModal(false)}
        membersIds={participants.map(participant => participant.memberId)}
      />
    </div>
  )
}
